import React, { lazy } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import Loadable from '../layouts/full/shared/loadable/Loadable';
import { useSelector } from 'react-redux';
import { AppState } from 'src/store/Store';
import TournamentWidget from 'src/views/widgets/tournamentWidget/tournamentWidget';
import { usePermissions } from 'src/hooks/usePermissions';
import { FederationsPermissions } from 'src/types/apps/permissions';

/* ***Layouts**** */
const FullLayout = Loadable(lazy(() => import('../layouts/full/FullLayout')));
const BlankLayout = Loadable(lazy(() => import('../layouts/blank/BlankLayout')));

const Calendar = Loadable(lazy(() => import('../views/apps/calendar/BigCalendar')));
const Faq = Loadable(lazy(() => import('../views/pages/faq/Faq')));
const AccountSetting = Loadable(
  lazy(() => import('../views/pages/account-setting/AccountSetting')),
);

const Coaches = Loadable(lazy(() => import('../views/pages/coaches/Coaches')));
const CoachesPackages = Loadable(
  lazy(() => import('../views/pages/coachesPackages/CoachesPackages')),
);
const CoachPackagesDashboard = Loadable(
  lazy(() => import('../views/pages/coach-packages-dashboard/CoachPackagesDashboard')),
);
const CoachPackageDetails = Loadable(
  lazy(() => import('../views/pages/coach-packages-dashboard/CoachPackageDetails')),
);
const Schedule = Loadable(lazy(() => import('../views/pages/schedule/Schedule')));
const LoyaltyProgram = Loadable(
  lazy(() => import('../views/pages/loyalty-program/LoyaltyProgram')),
);

// .. reporting
const Reporting = Loadable(lazy(() => import('../views/pages/reporting/Reporting')));
const ReportingCoaches = Loadable(
  lazy(() => import('../views/pages/reporting-coaches/ReportingCoaches')),
);

const Coupons = Loadable(lazy(() => import('../views/pages/coupons/Coupons')));
const Discounts = Loadable(lazy(() => import('../views/pages/discounts/Discounts')));
const MalaebLive = Loadable(lazy(() => import('../views/pages/malaeb-live/MalaebLiveBookings')));
const ViewVideo = Loadable(lazy(() => import('../views/pages/malaeb-live/ViewVideo')));
const ManageStadium = Loadable(lazy(() => import('../views/pages/manage-stadium/ManageStadium')));
const Gems = Loadable(lazy(() => import('../views/pages/gems/Gems')));
const GemBreakdown = Loadable(lazy(() => import('../views/pages/gems/components/GemBreakdown')));
const Invoices = Loadable(lazy(() => import('../views/pages/billing/Billing')));
const OnlinePayments = Loadable(
  lazy(() => import('../views/pages/billing/components/OnlinePayments')),
);
const OrdersPage = Loadable(lazy(() => import('../views/pages/billing/OrdersPage')));
const Tournaments = Loadable(lazy(() => import('../views/pages/tournaments/Tournaments')));
const Wallet = Loadable(lazy(() => import('../views/pages/wallet/Wallet')));
const SuccessPage = Loadable(lazy(() => import('../views/pages/success-page/SuccessPage')));
const ErrorPage = Loadable(lazy(() => import('../views/pages/error-page/ErrorPage')));
const WaitingList = Loadable(
  lazy(() => import('../views/pages/schedule/components/WaitingList/WaitingList')),
);
const FixedBookings = Loadable(lazy(() => import('../views/pages/fixed-bookings/FixedBookings')));
const BookingCategories = Loadable(lazy(() => import('../views/pages/booking-categories')));
const DynamicLinks = Loadable(lazy(() => import('../views/pages/dynamic-links')));
const CustomPaymentMethods = Loadable(lazy(() => import('../views/pages/custom-payment-methods')));
const Matches = Loadable(lazy(() => import('../views/pages/matches')));
const FederationUsers = Loadable(lazy(() => import('../views/pages/federation/FederationUsers')));
const MembershipsList = Loadable(lazy(() => import('../views/pages/federation/Memberships')));

// widget
const WidgetCards = Loadable(lazy(() => import('../views/widgets/cards/WidgetCards')));
const WidgetBanners = Loadable(lazy(() => import('../views/widgets/banners/WidgetBanners')));
const WidgetCharts = Loadable(lazy(() => import('../views/widgets/charts/WidgetCharts')));

// authentication
const Login = Loadable(lazy(() => import('../views/authentication/auth1/Login')));
const Login2 = Loadable(lazy(() => import('../views/authentication/auth2/Login2')));
const Register = Loadable(lazy(() => import('../views/authentication/auth1/Register')));
const Register2 = Loadable(lazy(() => import('../views/authentication/auth2/Register2')));
const ForgotPassword = Loadable(lazy(() => import('../views/authentication/auth1/ForgotPassword')));
const ResetPassword = Loadable(
  lazy(() => import('../views/authentication/reset-password/ResetPassword')),
);

const ForgotPassword2 = Loadable(
  lazy(() => import('../views/authentication/auth2/ForgotPassword2')),
);
const TwoSteps = Loadable(lazy(() => import('../views/authentication/auth1/TwoSteps')));
const TwoSteps2 = Loadable(lazy(() => import('../views/authentication/auth2/TwoSteps2')));
const Error = Loadable(lazy(() => import('../views/authentication/Error')));
const Maintenance = Loadable(lazy(() => import('../views/authentication/Maintenance')));

const WeeklyUpdate = Loadable(lazy(() => import('../views/pages/weeklyUpdate/WeeklyUpdate')));
const Releases = Loadable(lazy(() => import('../views/pages/releases/Releases')));

// landingpage
const Landingpage = Loadable(lazy(() => import('../views/pages/landingpage/Landingpage')));

const TournamentProfile = Loadable(
  lazy(() => import('../views/pages/tournaments/components/TournamentInfo/TournamentProfile')),
);

const AuthWrapper = (children: React.ReactNode) => {
  const location = useLocation();

  const isResetPasswordPage = location.pathname.startsWith('/auth/reset_password');

  const path = isResetPasswordPage ? location.pathname : null;

  // const { hasUsers } = useAuth(); // Assume this hook provides user information
  const user = useSelector((state: AppState) => state.authReducer?.user);

  if (!user) {
    return <Navigate to={isResetPasswordPage ? `${path}` : '/auth/login'} replace />;
  }

  return <FullLayout>{children}</FullLayout>;
};

const Router = (isFederation: boolean) => {
  if (isFederation) {
    return [
      {
        path: '/auth/reset_password/:token/:email',
        element: <ResetPassword />,
      },
      {
        path: '/',
        element: <AuthWrapper />,
        children: [
          { path: '/', element: <Navigate to="/pages/tournaments/active" /> },
          { path: '/pages/tournaments/:status', element: <Tournaments /> },
          { path: '/pages/tournaments/:status/:id', element: <TournamentProfile /> },
          { path: '/pages/:stadiumId/tournaments/:status/:id', element: <TournamentProfile /> },
          { path: '/pages/federation/users', element: <FederationUsers /> },
          { path: '/pages/federation/memberships', element: <MembershipsList /> },
          { path: '/pages/faq', element: <Faq /> },
          { path: '/pages/account-settings', element: <AccountSetting /> },
          { path: '*', element: <Navigate to="/auth/404" /> },
        ],
      },
    ];
  }

  return [
    {
      path: '/auth/reset_password/:token/:email',
      element: <ResetPassword />,
    },
    {
      path: '/',
      element: <AuthWrapper />,
      children: [
        { path: '/', element: <Navigate to="/pages/schedule" /> },
        { path: '/pages/schedule/fixed-bookings', element: <FixedBookings /> },
        { path: '/pages/waiting-list', element: <WaitingList /> },
        { path: '/pages/booking-categories', element: <BookingCategories /> },
        { path: '/pages/dynamic-links', element: <DynamicLinks /> },
        { path: '/pages/custom-payment-methods', element: <CustomPaymentMethods /> },
        { path: '/pages/matches', element: <Matches /> },
        { path: '/apps/calendar', element: <Calendar /> },
        { path: '/pages/faq', element: <Faq /> },
        { path: '/pages/account-settings', element: <AccountSetting /> },
        { path: '/widgets/cards', element: <WidgetCards /> },
        { path: '/widgets/banners', element: <WidgetBanners /> },
        { path: '/widgets/charts', element: <WidgetCharts /> },
        { path: '/pages/coaches', element: <Coaches /> },
        { path: '/pages/coach-packages', element: <CoachPackagesDashboard /> },
        { path: '/pages/coach-packages/:packageId/details', element: <CoachPackageDetails /> },
        { path: '/pages/discounts', element: <Discounts /> },
        { path: '/pages/coupons', element: <Coupons /> },
        { path: '/pages/:stadiumId/coaches/:id/packages', element: <CoachesPackages /> },
        { path: '/pages/:stadiumId/tournaments/:status/:id', element: <TournamentProfile /> },
        { path: '/pages/schedule', element: <Schedule /> },
        { path: '/pages/loyalty-program', element: <LoyaltyProgram /> },
        { path: '/pages/reporting', element: <Reporting /> },
        { path: '/pages/malaeb-live/malaeb-live-bookings', element: <MalaebLive /> },
        { path: '/pages/malaeb-live/videos', element: <ViewVideo /> },
        { path: '/pages/malaeb-live/video/:bookingId', element: <ViewVideo /> },
        { path: '/pages/manage-stadium', element: <ManageStadium /> },
        { path: '/pages/billing/invoices', element: <Invoices /> },
        { path: '/pages/billing/online-payments', element: <OnlinePayments /> },
        { path: '/pages/billing/orders', element: <OrdersPage /> },
        { path: '/pages/billing/orders/:orderId', element: <OrdersPage /> },
        { path: '/pages/billing/reporting-coaches', element: <ReportingCoaches /> },
        { path: '/pages/gems/manage-gems', element: <Gems /> },
        { path: '/pages/gems/breakdown', element: <GemBreakdown /> },
        { path: '/pages/tournaments/:status', element: <Tournaments /> },
        { path: '/pages/wallet', element: <Wallet /> },
        { path: '/pages/custom-payment-methods', element: <CustomPaymentMethods /> },
        { path: '/pages/weekly-update', element: <WeeklyUpdate /> },
        { path: '/pages/releases', element: <Releases /> },
        { path: '*', element: <Navigate to="/auth/404" /> },
      ],
    },
    {
      path: '/',
      element: <BlankLayout />,
      children: [
        { path: '/auth/404', element: <Error /> },
        { path: '/auth/login', element: <Login /> },
        { path: '/auth/login2', element: <Login2 /> },
        { path: '/auth/register', element: <Register /> },
        { path: '/auth/register2', element: <Register2 /> },
        { path: '/auth/forgot-password', element: <ForgotPassword /> },
        { path: '/auth/forgot-password2', element: <ForgotPassword2 /> },
        { path: '/auth/two-steps', element: <TwoSteps /> },
        { path: '/auth/two-steps2', element: <TwoSteps2 /> },
        { path: '/auth/maintenance', element: <Maintenance /> },
        { path: '/charge-wallet/failure/:id', element: <ErrorPage /> },
        { path: '/charge-wallet/success/:id', element: <SuccessPage /> },
        { path: '/landingpage', element: <Landingpage /> },
        { path: '*', element: <Navigate to="/auth/404" /> },
        //additional tournaments will be comma separated ids if not provided will be NA
        {
          path: '/tournaments/widget/:id/:tab/:additionalTournaments/:additionalTournamentsNames/:token',
          element: <TournamentWidget />,
        },
      ],
    },
  ];
};

export default Router;
